import React, { FunctionComponent, useEffect, useState } from 'react';
import FileDownload from 'js-file-download';
import { useParams } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { IAlertProps } from '../../interface/IAlertProps';
import { FormatTestJsonToText, getDefaultAlertProps } from '../../utils/utils';
import { IApiResult } from '../../interface/IApiResult';
import {
  API_STATUS_PROBLEM,
  DOWNLOAD_FILE_NUM,
  TEST_TYPE,
} from '../../constant/constants';
import { AlertSeverityEnum } from '../../enum/AlertSeverityEnum';
import { MyAlert } from '../UI/MyAlert';
import { ContentDto } from '../../dto/ContentDto';
import { TestDto } from '../../dto/TestDto';
import { apiGetTest } from '../../service/apiGetTest';
import { DialogTestItem } from './DialogTestItem';
import { apiDeleteContent } from '../../service/apiDeleteContent';
import { AddFile } from '../add-file/AddFile';
import { BackDropDiv } from '../backdrop/BackDropDiv';
import { apiGetTestNew } from '../../service/apiGetTestNew';
import { MyTextFiled } from '../UI/MyTextField';
import { apiGetTestFile } from '../../service/apiGetTestFile';
import { TextDto } from '../../dto/TextDto';
import { apiSaveTestNew } from '../../service/apiSaveTestNew';
import { DialogTestItemToAdd } from './DialogTestItemToAdd';
import { apiGetTestOld } from '../../service/apiGetTestOld';

interface Props {
  open: boolean;
  onClose: () => void;
  addTest: (e: any) => void;
  dto?: ContentDto;
}

export const DialogTestAddToAbstract: FunctionComponent<Props> = ({
  open,
  dto,
  addTest,
  onClose,
}) => {
  const [alertProps, setAlertProps] = useState<IAlertProps>(
    getDefaultAlertProps()
  );
  const [testString, setTestString] = useState('');
  const [testDtos, setTestDtos] = useState<TestDto[]>([]);
  const [backDrop, setBackDrop] = useState(false);
  const [selectedTests, setSelectedTests] = useState<Set<number>>(new Set());
  let numTest = 0;

  const handleClose = () => {
    setTestDtos([]);
    setSelectedTests(new Set());
    onClose();
  };

  const handleAddTest = () => {
    // Получаем только выбранные тесты
    const selectedTestDtos = testDtos.filter((_, index) =>
      selectedTests.has(index)
    );

    if (selectedTestDtos.length === 0) {
      setAlertProps({
        message: 'Выберите хотя бы один тест',
        severity: AlertSeverityEnum.warning,
      });
      return;
    }

    // Передаем выбранные тесты
    addTest(selectedTestDtos);
    handleClose();
  };

  // ИЗМЕНЕНО: Функция для обработки выбора теста
  const handleTestSelect = (index: number, checked: boolean) => {
    setSelectedTests((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(index);
      } else {
        newSet.delete(index);
      }
      return newSet;
    });
  };

  const { section } = useParams();

  const getTest = async (route: string, key: string, download?: number) => {
    setBackDrop(true);
    const apiResult: IApiResult = await apiGetTestOld(route, key, download);
    if (apiResult.isSuccess) {
      setTestDtos(apiResult.data.test);
    } else {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
    setBackDrop(false);
  };

  useEffect(() => {
    if (open) {
      getTest(section ?? '', dto?.key ?? '');
      setSelectedTests(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div>
      <Dialog
        open={open}
        fullWidth
        maxWidth="xl"
        style={{ userSelect: 'none' }}
        PaperProps={{
          sx: {
            backgroundColor: '#e7ebee',
            maxWidth: '90%',
            maxHeight: '90%',
            height: '90%',
          },
        }}
      >
        <BackDropDiv open={backDrop} />
        <MyAlert
          message={alertProps.message}
          severity={alertProps.severity}
          onClose={() => setAlertProps({ ...alertProps, message: '' })}
        />
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              style={{
                fontFamily: 'sans-serif',
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '24px',
                color: '#000000',
                letterSpacing: 1,
              }}
              component="text"
            >
              {`Тесты - ${dto?.key} ${dto?.title}`}
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton
                title="Добавить тесты"
                size="small"
                onClick={() => handleAddTest()}
              >
                <SaveIcon fontSize="large" sx={{ color: '#2196f3' }} />
              </IconButton>
              <IconButton title="Закрыть" size="small" onClick={handleClose}>
                <CloseIcon fontSize="large" sx={{ color: '#000000' }} />
              </IconButton>
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{
            padding: 0,
            '&:first-of-type': { paddingTop: 0 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden', // ← Отключаем прокрутку диалога
            flex: 1,
          }}
        >
          <Stack
            spacing={1}
            padding="20px"
            sx={{ height: '100%', overflow: 'auto' }}
          >
            {testDtos.map((item) => {
              const currentIndex = numTest;
              numTest += 1;
              return (
                <div key={currentIndex}>
                  <DialogTestItemToAdd
                    isEdit={false}
                    num={currentIndex + 1}
                    keyTitle={dto?.key ?? ''}
                    titleName={dto?.title ?? ''}
                    dto={item}
                    // ИЗМЕНЕНО: Передаем пропсы для чекбокса
                    checked={selectedTests.has(currentIndex)}
                    onSelect={(checked) =>
                      handleTestSelect(currentIndex, checked)
                    }
                  />
                  <Divider />
                </div>
              );
            })}
          </Stack>
        </DialogContent>
      </Dialog>
    </div>
  );
};

DialogTestAddToAbstract.defaultProps = {
  dto: undefined,
};
